import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import ModalLayout from '../../../../../shared/components/structure/ModalLayout';
import useCSVHandler from '../../../../../shared/hooks/UseCSVHandler';
import { getSparkBaseUrl } from '../../../../../shared/constants/Urls';
import {
    color_defaults_white,
    color_shades_darkest,
    color_shades_darker,
    color_text_dashboard_muted,
    color_shades_light,
    color_shades_lighter,
    voicify_spark_darker,
    voicify_spark_darkest,
    voicify_spark_dark,
    voicify_spark_medium,
    voicify_spark_light,
    voicify_spark_lightest,
    button_shadow_light
} from '../../../../../shared/constants/colors';
import { breakpoint_macbook_small, breakpoint_large } from '../../../../../shared/constants/breakpoints';

export interface AppBreakdownDetail {
    appName: string;
    count: number;
    english?: number;
    spanish?: number;
}

export interface ChartDetailData {
    title: string;
    value: number;
    subtitle?: string;
    apps: AppBreakdownDetail[];
}

/** Transcript record for showing detailed call information */
export interface TranscriptRecord {
    id: string;
    date: string;
    time: string;
    phoneLastFour: string;
    location: string;
    applicationId: string;
    sessionId: string;
    category?: string;
    tags?: string[];
    eventCountTags?: string[];
    sessionSummaries?: string;
}

interface ChartDetailModalProps {
    isVisible: boolean;
    onClose: () => void;
    title: string;
    dateRange: {
        startDate: string;
        endDate: string;
    };
    data: ChartDetailData | ChartDetailData[];
    valueLabel?: string;
    countPrefix?: string;
    showLanguageBreakdown?: boolean;
    showPercentOfTotal?: boolean;
    /** Custom label for the summary card. When provided, overrides "Total {valueLabel}" */
    summaryLabel?: string;
    /** Show individual totals for each data item when multiple items are provided */
    showIndividualTotals?: boolean;
    /** Optional transcript records to display in a separate tab */
    transcriptRecords?: TranscriptRecord[];
    /** Whether to show the transcripts tab (requires transcriptRecords) */
    showTranscriptsTab?: boolean;
    /** When true, displays ES badge and hides language breakdown (data is Spanish-only) */
    spanishOnly?: boolean;
    /** When true, hides the blue summary card at the top of the modal */
    hideSummary?: boolean;
    /** Default text to show when no sessionSummaries exist (displayed as a bullet) */
    noSummaryText?: string;
    /** Domain context for category-specific fallback messages ('FOOD_ORDERING' | 'HEALTHCARE') */
    domain?: 'FOOD_ORDERING' | 'HEALTHCARE';
    /** Label for the event type in footer (e.g., "booking", "reschedule", "cancellation"). When provided, shows "X [label(s)] in Y conversations" */
    eventLabel?: string;
    /** Tag(s) to count for multipatient booking support. When provided, counts tag occurrences instead of transcript records */
    eventCountTags?: string[];
    /** When true, CSV export includes all records regardless of category filter (for "Attempts" modals) */
    exportAllCategories?: boolean;
    /** Custom display name for success categories (Success, Event, Booking) in filter chips and category pills.
     * When provided, replaces "Success" with this value (e.g., "Booking", "Reschedule", "Scheduling Event") */
    successCategoryDisplayName?: string;
    /** Custom info text to display in the header (top right area). 
     * When provided, displays this text; otherwise the modal computes it from data. */
    headerInfoText?: string;
}

/**
 * ChartDetailModal - A reusable modal component for displaying detailed chart data breakdown
 * 
 * This modal extends the hover tooltip functionality to show:
 * - Detailed breakdown by location/app
 * - Language breakdown (English vs Spanish) when available
 * - Totals and percentages
 * - CSV export capability
 */
const ChartDetailModal: React.FC<ChartDetailModalProps> = ({
    isVisible,
    onClose,
    title,
    dateRange,
    data,
    valueLabel = 'calls',
    countPrefix = '',
    showLanguageBreakdown = true,
    showPercentOfTotal = true,
    summaryLabel,
    showIndividualTotals = false,
    transcriptRecords = [],
    showTranscriptsTab = false,
    spanishOnly = false,
    hideSummary = false,
    noSummaryText,
    domain,
    eventLabel,
    eventCountTags,
    exportAllCategories = false,
    successCategoryDisplayName,
    headerInfoText
}) => {
    const csvHandler = useCSVHandler();
    const dataArray = data ? (Array.isArray(data) ? data : [data]) : [];

    // Helper to get category-specific fallback message based on domain
    const getFallbackMessage = (category?: string): string => {
        if (category === 'NO_INTERACTION') {
            return domain === 'HEALTHCARE'
                ? "The patient didn't interact with the assistant."
                : "The customer didn't interact with the assistant.";
        }
        if (category === 'IMMEDIATE_TRANSFER') {
            return domain === 'HEALTHCARE'
                ? "The patient immediately asked to transfer."
                : "The customer immediately asked to transfer.";
        }
        return noSummaryText || 'Summary unavailable';
    };

    // Track which locations are expanded
    const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());

    // Track which transcript rows are expanded
    const [expandedTranscripts, setExpandedTranscripts] = useState<Set<string>>(new Set());

    // Category filter state - supports multi-select (e.g., "Attempt", "Success")
    const [selectedCategoryFilters, setSelectedCategoryFilters] = useState<Set<string>>(new Set());

    // Handle modal close - reset internal state and call parent's onClose
    const handleClose = () => {
        setExpandedLocations(new Set());
        setExpandedTranscripts(new Set());
        setSelectedCategoryFilters(new Set());
        onClose();
    };

    // Toggle expansion of a location (also expands/collapses session summaries)
    const toggleLocationExpansion = (location: string) => {
        const isCurrentlyExpanded = expandedLocations.has(location);

        setExpandedLocations(prev => {
            const newSet = new Set(prev);
            if (newSet.has(location)) {
                newSet.delete(location);
            } else {
                newSet.add(location);
            }
            return newSet;
        });

        // Also expand/collapse all transcripts in this location that have expandable content
        const locationTranscripts = transcriptsByLocation.get(location) || [];
        const transcriptIdsToToggle = locationTranscripts
            .filter(t => t.sessionSummaries || noSummaryText)
            .map(t => t.id);

        if (transcriptIdsToToggle.length > 0) {
            setExpandedTranscripts(prev => {
                const newSet = new Set(prev);
                if (isCurrentlyExpanded) {
                    // Collapse all transcripts in this location
                    transcriptIdsToToggle.forEach(id => newSet.delete(id));
                } else {
                    // Expand all transcripts in this location
                    transcriptIdsToToggle.forEach(id => newSet.add(id));
                }
                return newSet;
            });
        }
    };

    // Toggle expansion of a transcript row
    const toggleTranscriptExpansion = (transcriptId: string) => {
        setExpandedTranscripts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(transcriptId)) {
                newSet.delete(transcriptId);
            } else {
                newSet.add(transcriptId);
            }
            return newSet;
        });
    };

    // Parse sessionSummaries JSON string into array
    // Format: {"uuid1": "summary1", "uuid2": "summary2", ...}
    // Orders so that the entry matching sessionId comes last (highest call number)
    const parseSessionSummaries = (summariesJson?: string, sessionId?: string): string[] => {
        if (!summariesJson) return [];
        try {
            const parsed = JSON.parse(summariesJson);
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                // It's an object with UUID keys and summary values
                const entries = Object.entries(parsed) as [string, string][];
                // Sort so that the entry matching sessionId comes last
                entries.sort((a, b) => {
                    const aMatches = a[0] === sessionId;
                    const bMatches = b[0] === sessionId;
                    if (aMatches && !bMatches) return 1;  // a goes last
                    if (!aMatches && bMatches) return -1; // b goes last
                    return 0;
                });
                return entries.map(([, value]) => value);
            }
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    // Render summary text with bullet points for lines starting with * or -
    const renderSummaryWithBullets = (summary: string) => {
        const lines = summary.split('\n').filter(line => line.trim());
        const isBulletLine = (line: string) => /^\s*[-*•]\s/.test(line);
        const stripBullet = (line: string) => line.trim().replace(/^[-*•]\s*/, '');
        const bulletLines = lines.filter(isBulletLine);

        // If all non-empty lines are bullet points, render as a list
        if (bulletLines.length > 0 && bulletLines.length === lines.length) {
            return (
                <SummaryBulletList>
                    {lines.map((line, i) => (
                        <SummaryBulletItem key={i}>
                            {stripBullet(line)}
                        </SummaryBulletItem>
                    ))}
                </SummaryBulletList>
            );
        }

        // Mixed content - render bullets as list, other text as paragraphs
        const elements: React.ReactNode[] = [];
        let currentBullets: string[] = [];

        lines.forEach((line, i) => {
            if (isBulletLine(line)) {
                currentBullets.push(stripBullet(line));
            } else {
                // Flush any accumulated bullets
                if (currentBullets.length > 0) {
                    elements.push(
                        <SummaryBulletList key={`bullets-${i}`}>
                            {currentBullets.map((bullet, j) => (
                                <SummaryBulletItem key={j}>{bullet}</SummaryBulletItem>
                            ))}
                        </SummaryBulletList>
                    );
                    currentBullets = [];
                }
                elements.push(<SummaryParagraph key={`para-${i}`}>{line}</SummaryParagraph>);
            }
        });

        // Flush remaining bullets
        if (currentBullets.length > 0) {
            elements.push(
                <SummaryBulletList key="bullets-end">
                    {currentBullets.map((bullet, j) => (
                        <SummaryBulletItem key={j}>{bullet}</SummaryBulletItem>
                    ))}
                </SummaryBulletList>
            );
        }

        return <>{elements}</>;
    };

    // Determine if transcripts should be shown (expandable rows)
    const hasTranscripts = showTranscriptsTab && transcriptRecords.length > 0;

    // Helper to convert 12-hour time (h:mm A) to 24-hour sortable format
    const convertTo24Hour = (time12: string): string => {
        const m = moment(time12, 'h:mm A');
        return m.isValid() ? m.format('HH:mm') : time12;
    };

    // Group transcripts by location, sorted by date+time descending (most recent first)
    const transcriptsByLocation = useMemo(() => {
        // Apply category filters if any selected
        const filteredRecords = selectedCategoryFilters.size > 0
            ? transcriptRecords.filter(t => t.category && selectedCategoryFilters.has(t.category))
            : transcriptRecords;

        const map = new Map<string, TranscriptRecord[]>();
        filteredRecords.forEach(t => {
            const existing = map.get(t.location) || [];
            existing.push(t);
            map.set(t.location, existing);
        });
        // Sort each location's transcripts by date+time descending
        map.forEach((transcripts, location) => {
            transcripts.sort((a, b) => {
                const aDateTime = `${a.date} ${convertTo24Hour(a.time)}`;
                const bDateTime = `${b.date} ${convertTo24Hour(b.time)}`;
                return bDateTime.localeCompare(aDateTime); // descending
            });
        });
        return map;
    }, [transcriptRecords, selectedCategoryFilters]);

    // Get unique categories from transcripts for filter chips
    const uniqueCategories = useMemo(() => {
        const categories = new Set<string>();
        transcriptRecords.forEach(t => {
            if (t.category) {
                categories.add(t.category);
            }
        });
        return Array.from(categories).sort();
    }, [transcriptRecords]);

    // Default to "Success", "Booking", or "Event" filter when categories become available
    // (Event and Booking both display as "Success")
    useEffect(() => {
        if (uniqueCategories.length > 1 && selectedCategoryFilters.size === 0) {
            if (uniqueCategories.includes('Success')) {
                setSelectedCategoryFilters(new Set(['Success']));
            } else if (uniqueCategories.includes('Event')) {
                setSelectedCategoryFilters(new Set(['Event']));
            } else if (uniqueCategories.includes('Booking')) {
                setSelectedCategoryFilters(new Set(['Booking']));
            } else {
                setSelectedCategoryFilters(new Set([uniqueCategories[0]]));
            }
        }
    }, [uniqueCategories, selectedCategoryFilters]);

    // Count transcripts by category for filter chip labels
    // For success-type categories with eventCountTags, count tag occurrences (events) instead of conversations
    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        const successCategories = ['Event', 'Booking', 'Success'];
        transcriptRecords.forEach(t => {
            if (t.category) {
                // For success-type categories, count tag occurrences from the modal-level eventCountTags prop
                // This supports multipatient bookings where one conversation can have multiple events
                if (eventCountTags && eventCountTags.length > 0 && successCategories.includes(t.category) && t.tags && Array.isArray(t.tags)) {
                    const tagOccurrences = t.tags.filter(tag => eventCountTags.includes(tag)).length;
                    counts[t.category] = (counts[t.category] || 0) + tagOccurrences;
                } else {
                    counts[t.category] = (counts[t.category] || 0) + 1;
                }
            }
        });
        return counts;
    }, [transcriptRecords, eventCountTags]);

    // Map category names to display names (Event→custom, Booking→custom, Success→custom)
    // Uses successCategoryDisplayName prop when provided, otherwise falls back to "Success"
    const getCategoryDisplayName = (category: string): string => {
        if (category === 'Event' || category === 'Booking' || category === 'Success') {
            return successCategoryDisplayName || 'Success';
        }
        return category;
    };

    // Plural version of getCategoryDisplayName for "% of X" text
    const getCategoryDisplayNamePlural = (category: string): string => {
        const singular = getCategoryDisplayName(category);
        const lowerSingular = singular.toLowerCase();
        // Handle special cases
        if (lowerSingular === 'success') return 'Success';
        if (lowerSingular === 'attempt') return 'Attempts';
        // Handle "noun verb" patterns where we pluralize the noun
        if (lowerSingular === 'appointment managed') return 'Appointments Managed';
        if (lowerSingular === 'appointment booked') return 'Appointments Booked';
        if (lowerSingular === 'appointment cancelled') return 'Appointments Cancelled';
        if (lowerSingular === 'appointment rescheduled') return 'Appointments Rescheduled';
        if (lowerSingular === 'appointment check') return 'Appointment Checks';
        // Default: add 's' for pluralization
        return `${singular}s`;
    };

    // Filtered transcript count for footer display
    const filteredTranscriptCount = useMemo(() => {
        if (selectedCategoryFilters.size === 0) return transcriptRecords.length;
        return transcriptRecords.filter(t => t.category && selectedCategoryFilters.has(t.category)).length;
    }, [transcriptRecords, selectedCategoryFilters]);

    // Event count that accounts for multipatient bookings (counts tag occurrences when eventCountTags provided)
    const filteredEventCount = useMemo(() => {
        // Filter transcripts by category first
        const filteredRecords = selectedCategoryFilters.size === 0
            ? transcriptRecords
            : transcriptRecords.filter(t => t.category && selectedCategoryFilters.has(t.category));

        // If eventCountTags provided, count tag occurrences (for multipatient booking support)
        if (eventCountTags && eventCountTags.length > 0) {
            let tagCount = 0;
            filteredRecords.forEach(t => {
                if (t.tags && Array.isArray(t.tags)) {
                    tagCount += t.tags.filter(tag => eventCountTags.includes(tag)).length;
                }
            });
            return tagCount;
        }

        // Otherwise just count records
        return filteredRecords.length;
    }, [transcriptRecords, selectedCategoryFilters, eventCountTags]);

    // Unique conversation count from filtered transcripts (for eventLabel footer format)
    const filteredConversationCount = useMemo(() => {
        const filteredRecords = selectedCategoryFilters.size === 0
            ? transcriptRecords
            : transcriptRecords.filter(t => t.category && selectedCategoryFilters.has(t.category));
        const uniqueSessionIds = new Set(filteredRecords.map(t => t.sessionId));
        return uniqueSessionIds.size;
    }, [transcriptRecords, selectedCategoryFilters]);

    // Helper to pluralize eventLabel
    const pluralizeEventLabel = (label: string, count: number): string => {
        if (count === 1) return label;
        // Handle special cases for "noun verb" patterns where we pluralize the noun
        const lowerLabel = label.toLowerCase();
        if (lowerLabel === 'appointment check') return 'appointment checks';
        if (lowerLabel === 'appointment managed') return 'appointments managed';
        if (lowerLabel === 'appointment booked') return 'appointments booked';
        if (lowerLabel === 'appointment cancelled') return 'appointments cancelled';
        if (lowerLabel === 'appointment rescheduled') return 'appointments rescheduled';
        // Default: add 's'
        return `${label}s`;
    };

    // Calculate totals across all data
    const totals = useMemo(() => {
        let totalValue = 0;
        let totalEnglish = 0;
        let totalSpanish = 0;

        dataArray.forEach(item => {
            totalValue += item.value;
            item.apps.forEach(app => {
                if (app.english !== undefined) totalEnglish += app.english;
                if (app.spanish !== undefined) totalSpanish += app.spanish;
            });
        });

        return { totalValue, totalEnglish, totalSpanish };
    }, [dataArray]);

    // Flatten app data for the table, grouped by location
    const tableData = useMemo(() => {
        const locationMap = new Map<string, {
            location: string;
            totalCount: number;
            totalEnglish: number;
            totalSpanish: number;
        }>();

        dataArray.forEach(item => {
            item.apps.forEach(app => {
                if (!locationMap.has(app.appName)) {
                    locationMap.set(app.appName, {
                        location: app.appName,
                        totalCount: 0,
                        totalEnglish: 0,
                        totalSpanish: 0
                    });
                }
                const locationData = locationMap.get(app.appName)!;
                // When spanishOnly is active use the Spanish count as the display count
                // so the modal table matches the chart bar and tooltip values.
                locationData.totalCount += spanishOnly ? (app.spanish ?? app.count) : app.count;
                if (app.english !== undefined) locationData.totalEnglish += app.english;
                if (app.spanish !== undefined) locationData.totalSpanish += app.spanish;
            });
        });

        // Sort by total count descending
        return Array.from(locationMap.values()).sort((a, b) => b.totalCount - a.totalCount);
    }, [dataArray, spanishOnly]);

    // Calculate the actual total from tableData for percentage calculations
    // This ensures percentages are based on the sum of displayed values, never exceeding 100%
    const tableTotalCount = useMemo(() => {
        return tableData.reduce((sum, location) => sum + location.totalCount, 0);
    }, [tableData]);

    // Build display data based on filtered transcripts when transcripts are present.
    //
    // Two strategies are used depending on whether multiple categories exist:
    //
    // 1. MULTI-CATEGORY (e.g. "Attempt" + "Appointment Managed"): recompute counts
    //    directly from the filtered transcript records so per-location numbers
    //    correctly reflect whichever category pill is active.  The apps array
    //    passed to the modal typically only contains completion counts, so we
    //    can't rely on tableData when an "Attempt" filter is selected.
    //
    // 2. SINGLE-CATEGORY or NO TRANSCRIPTS (e.g. Average Ticket Size with
    //    "Placed Order" only): use tableData counts, which may hold non-
    //    conversation values such as dollar amounts.  Transcripts are used
    //    only to determine which locations to show.
    const displayData = useMemo(() => {
        if (!hasTranscripts) {
            return tableData;
        }

        const hasMultipleCategories = uniqueCategories.length > 1;

        if (hasMultipleCategories) {
            // Recompute per-location counts from filtered transcripts so pill
            // selection changes the displayed numbers correctly.
            const successCategoryNames = ['Event', 'Booking', 'Success'];

            return Array.from(transcriptsByLocation.entries())
                .map(([location, transcripts]) => {
                    let count = 0;
                    let english = 0;
                    let spanish = 0;

                    transcripts.forEach(t => {
                        const isSuccess = t.category && successCategoryNames.includes(t.category);
                        const isSpanish = t.tags && t.tags.includes('SPANISH');

                        if (isSuccess && eventCountTags && eventCountTags.length > 0 && t.tags) {
                            // For success-type transcripts, count tag occurrences (multipatient support)
                            const tagOccurrences = t.tags.filter(tag => eventCountTags.includes(tag)).length;
                            count += tagOccurrences;
                            if (isSpanish) {
                                spanish += tagOccurrences;
                            } else {
                                english += tagOccurrences;
                            }
                        } else {
                            // For attempt or non-eventCountTag transcripts, count conversations
                            count += 1;
                            if (isSpanish) {
                                spanish += 1;
                            } else {
                                english += 1;
                            }
                        }
                    });

                    return {
                        location,
                        totalCount: count,
                        totalEnglish: english,
                        totalSpanish: spanish
                    };
                })
                .sort((a, b) => b.totalCount - a.totalCount);
        }

        // Single-category: use tableData counts (preserves non-conversation values
        // such as dollar amounts for Average Ticket Size) and use transcripts only
        // to determine which locations to display.
        const dataByLocation = new Map<string, { count: number; english: number; spanish: number }>();
        tableData.forEach(item => {
            dataByLocation.set(item.location, {
                count: item.totalCount,
                english: item.totalEnglish,
                spanish: item.totalSpanish
            });
        });
        const locationsFromTranscripts = new Set<string>();
        transcriptsByLocation.forEach((_, location) => {
            locationsFromTranscripts.add(location);
        });
        return Array.from(locationsFromTranscripts)
            .map((location) => {
                const locData = dataByLocation.get(location);
                return {
                    location,
                    totalCount: locData?.count ?? (transcriptsByLocation.get(location)?.length ?? 0),
                    totalEnglish: locData?.english ?? 0,
                    totalSpanish: locData?.spanish ?? 0
                };
            })
            .sort((a, b) => b.totalCount - a.totalCount);
    }, [hasTranscripts, uniqueCategories, tableData, transcriptsByLocation, eventCountTags]);

    // Calculate total from displayData for percentage calculations (accounts for multipatient bookings)
    const displayDataTotalCount = useMemo(() => {
        return displayData.reduce((sum, location) => sum + location.totalCount, 0);
    }, [displayData]);

    // Determine if we have language data (don't show breakdown if spanishOnly is active)
    const hasLanguageData = useMemo(() => {
        return showLanguageBreakdown && !spanishOnly && dataArray.some(item =>
            item.apps.some(app => app.english !== undefined || app.spanish !== undefined)
        );
    }, [dataArray, showLanguageBreakdown]);

    // Helper to format hours as "Xh Ym" for consistent display
    const formatHoursAndMinutes = (hours: number): string => {
        if (hours === undefined || hours === null) return '0h 0m';
        const wholeHours = Math.floor(hours);
        const minutes = Math.round((hours - wholeHours) * 60);
        if (wholeHours === 0) {
            return `${minutes}m`;
        }
        return `${wholeHours}h ${minutes}m`;
    };

    // Compute default header info text (same logic as was in the footer)
    // This provides context about what data is being displayed
    const computedHeaderInfoText = useMemo(() => {
        if (hasTranscripts) {
            if (eventLabel) {
                // Only show event label text when Success alone is selected (not with Attempt)
                const showEventFormat = selectedCategoryFilters.size === 1 &&
                    (selectedCategoryFilters.has('Event') || selectedCategoryFilters.has('Booking') || selectedCategoryFilters.has('Success'));
                if (showEventFormat) {
                    return `${filteredEventCount.toLocaleString()} ${pluralizeEventLabel(eventLabel, filteredEventCount)} in ${filteredConversationCount.toLocaleString()} conversation${filteredConversationCount !== 1 ? 's' : ''}`;
                }
                return null;
            }
            // For transfers, just show count without "in X conversations" suffix
            if (valueLabel === 'transfers') {
                return `${filteredTranscriptCount.toLocaleString()} transfers`;
            }
            const categoryText = selectedCategoryFilters.size > 0
                ? ` (${Array.from(selectedCategoryFilters).map(getCategoryDisplayName).join(' & ')})`
                : '';
            return `${filteredTranscriptCount.toLocaleString()} ${valueLabel} in ${filteredConversationCount.toLocaleString()} conversation${filteredConversationCount !== 1 ? 's' : ''}${categoryText}`;
        }
        // Non-transcript mode: location summary
        const locationText = `${tableData.length} location${tableData.length !== 1 ? 's' : ''}`;
        if (totals.totalValue > 0) {
            const valueText = valueLabel.includes('hours')
                ? formatHoursAndMinutes(totals.totalValue) + ' saved'
                : totals.totalValue.toLocaleString() + ' ' + valueLabel;
            return `${locationText} • ${valueText}`;
        }
        return locationText;
    }, [hasTranscripts, eventLabel, selectedCategoryFilters, filteredEventCount, filteredConversationCount,
        filteredTranscriptCount, valueLabel, tableData.length, totals.totalValue, getCategoryDisplayName]);

    // Use headerInfoText prop if provided, otherwise use computed default
    const displayHeaderInfoText = headerInfoText !== undefined ? headerInfoText : computedHeaderInfoText;

    // Handle CSV export
    const handleExportCSV = () => {
        const dateRangeStr = `${moment(dateRange.startDate).format('MMM D, YYYY')} - ${moment(dateRange.endDate).format('MMM D, YYYY')}`;
        const baseUrl = getSparkBaseUrl() || window.location.origin;

        // If we have transcript records, export call-level detail instead of summary
        if (hasTranscripts && transcriptRecords && transcriptRecords.length > 0) {
            // Apply category filters if any selected, unless exportAllCategories is true
            const recordsToExport = (!exportAllCategories && selectedCategoryFilters.size > 0)
                ? transcriptRecords.filter(r => r.category && selectedCategoryFilters.has(r.category))
                : transcriptRecords;

            const callHeaders = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'Category'];
            const callExportData = recordsToExport.map(record => ({
                'Date': record.date,
                'Time': record.time,
                'Phone (Last 4)': `***${record.phoneLastFour}`,
                'Location': record.location,
                'Transcript Link': `${baseUrl}/v/apps/${record.applicationId}/analytics/sessions?sessionId=${record.sessionId}&startDate=${encodeURIComponent(moment(dateRange.startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(dateRange.endDate).endOf('day').toISOString())}`,
                'Category': record.category || ''
            }));

            const filterSuffix = (!exportAllCategories && selectedCategoryFilters.size > 0) ? `-${Array.from(selectedCategoryFilters).join('-').toLowerCase()}` : '';
            const filename = `${title.toLowerCase().replace(/\s+/g, '-')}${filterSuffix}-calls-${moment(dateRange.startDate).format('YYYY-MM-DD')}-to-${moment(dateRange.endDate).format('YYYY-MM-DD')}`;
            csvHandler.exportCSV(callExportData, callHeaders, filename);
            return;
        }

        // Otherwise export location summary
        const baseHeaders = ['Date Range', 'Location', 'Total'];
        const languageHeaders = hasLanguageData ? ['English', 'Spanish'] : [];
        const percentHeader = showPercentOfTotal ? ['% of Total'] : [];
        const headers = [...baseHeaders, ...languageHeaders, ...percentHeader];

        const exportData: Record<string, string | number>[] = [];

        tableData.forEach(location => {
            const row: Record<string, string | number> = {
                'Date Range': dateRangeStr,
                'Location': location.location,
                'Total': location.totalCount
            };
            if (hasLanguageData) {
                row['English'] = location.totalEnglish;
                row['Spanish'] = location.totalSpanish;
            }
            if (showPercentOfTotal) {
                row['% of Total'] = tableTotalCount !== 0 ? `${Math.round((location.totalCount / tableTotalCount) * 100)}%` : '0%';
            }
            exportData.push(row);
        });

        // Add grand total row
        const grandTotalRow: Record<string, string | number> = {
            'Date Range': dateRangeStr,
            'Location': 'ALL LOCATIONS',
            'Total': tableTotalCount
        };
        if (hasLanguageData) {
            grandTotalRow['English'] = totals.totalEnglish;
            grandTotalRow['Spanish'] = totals.totalSpanish;
        }
        if (showPercentOfTotal) {
            grandTotalRow['% of Total'] = '100%';
        }
        exportData.push(grandTotalRow);

        const filename = `${title.toLowerCase().replace(/\s+/g, '-')}-breakdown-${moment(dateRange.startDate).format('YYYY-MM-DD')}-to-${moment(dateRange.endDate).format('YYYY-MM-DD')}`;
        csvHandler.exportCSV(exportData, headers, filename);
    };

    const formatNumber = (num: number, includeUnit: boolean = true): string => {
        if (countPrefix === '$') {
            return `${countPrefix}${num.toFixed(2)}`;
        }
        // For hours, round to nearest whole number and optionally add unit
        if (valueLabel.includes('hours')) {
            return includeUnit ? `${Math.round(num)} hours` : `${Math.round(num)}`;
        }
        return `${countPrefix}${num.toLocaleString()}`;
    };

    return (
        <ModalLayout
            isVisible={isVisible}
            onClose={handleClose}
            zIndex={1000}
        >
            <ModalContent>
                <ModalHeader>
                    <TitleSection>
                        <ModalTitleContainer>
                            <ModalTitle>{title}</ModalTitle>
                            {spanishOnly && (
                                <SpanishBadge>ES</SpanishBadge>
                            )}
                        </ModalTitleContainer>
                        <DateRange>
                            {moment(dateRange.startDate).format('MMM D, YYYY')} - {moment(dateRange.endDate).format('MMM D, YYYY')}
                        </DateRange>
                    </TitleSection>
                    <HeaderRightSection>
                        {displayHeaderInfoText && (
                            <HeaderInfoText>{displayHeaderInfoText}</HeaderInfoText>
                        )}
                        <CloseButton onClick={handleClose}>×</CloseButton>
                    </HeaderRightSection>
                </ModalHeader>

                {/* Summary Section - conditionally rendered, hidden when transcripts with multiple categories */}
                {!hideSummary && !(hasTranscripts && uniqueCategories.length > 1) && (
                    <SummarySection>
                        <SummaryCard isPrimary>
                            <SummaryLabel isPrimary>{summaryLabel || `Total ${valueLabel}`}</SummaryLabel>
                            <SummaryValue isPrimary>{formatNumber(totals.totalValue)}</SummaryValue>
                            {hasLanguageData && !hasTranscripts && (
                                <SummarySubtext isPrimary>
                                    {totals.totalEnglish.toLocaleString()} English • {totals.totalSpanish.toLocaleString()} Spanish
                                </SummarySubtext>
                            )}
                        </SummaryCard>
                    </SummarySection>
                )}

                {/* Category Filter Chips */}
                {hasTranscripts && uniqueCategories.length > 1 && (
                    <FilterSection>
                        <FilterLabel>Filter by:</FilterLabel>
                        <FilterChips>
                            {uniqueCategories.map(category => (
                                <FilterChip
                                    key={category}
                                    isActive={selectedCategoryFilters.has(category)}
                                    onClick={() => {
                                        const newFilters = new Set(selectedCategoryFilters);
                                        if (newFilters.has(category)) {
                                            // Don't allow deselecting if it's the only one selected
                                            if (newFilters.size > 1) {
                                                newFilters.delete(category);
                                            }
                                        } else {
                                            newFilters.add(category);
                                        }
                                        setSelectedCategoryFilters(newFilters);
                                    }}
                                    category={category}
                                >
                                    {getCategoryDisplayName(category)} ({categoryCounts[category] || 0})
                                </FilterChip>
                            ))}
                        </FilterChips>
                    </FilterSection>
                )}

                {/* Location List with Expandable Sessions */}
                <ScrollContainer>
                    {displayData.length === 0 ? (
                        <EmptyState>
                            <EmptyStateTitle>No data available</EmptyStateTitle>
                            <EmptyStateText>There is no {valueLabel} data for the selected date range.</EmptyStateText>
                        </EmptyState>
                    ) : (
                        <LocationsList>
                            {displayData.map((location, locIdx) => {
                                const isExpanded = expandedLocations.has(location.location);
                                const locationTranscripts = transcriptsByLocation.get(location.location) || [];
                                const hasLocationTranscripts = hasTranscripts && locationTranscripts.length > 0;

                                return (
                                    <LocationItem key={location.location}>
                                        <LocationHeader
                                            onClick={() => hasLocationTranscripts && toggleLocationExpansion(location.location)}
                                            onKeyDown={(e) => {
                                                if (hasLocationTranscripts && (e.key === 'Enter' || e.key === ' ')) {
                                                    e.preventDefault();
                                                    toggleLocationExpansion(location.location);
                                                }
                                            }}
                                            tabIndex={hasLocationTranscripts ? 0 : -1}
                                            role={hasLocationTranscripts ? 'button' : undefined}
                                            aria-expanded={hasLocationTranscripts ? isExpanded : undefined}
                                            isExpandable={hasLocationTranscripts}
                                            isExpanded={isExpanded}
                                        >
                                            <LocationRank>{locIdx + 1}</LocationRank>
                                            <LocationInfo>
                                                <LocationNameExpand>{location.location}</LocationNameExpand>
                                            </LocationInfo>
                                            <LocationStats>
                                                {hasLanguageData && (
                                                    <LanguageBreakdownSmall>
                                                        <LangItem>
                                                            <LangLabel>English</LangLabel>
                                                            <LangValue>{location.totalEnglish.toLocaleString()}</LangValue>
                                                        </LangItem>
                                                        <LangItem>
                                                            <LangLabel>Spanish</LangLabel>
                                                            <LangValue>{location.totalSpanish.toLocaleString()}</LangValue>
                                                        </LangItem>
                                                    </LanguageBreakdownSmall>
                                                )}
                                                <LocationTotal>
                                                    <TotalValue>{formatNumber(location.totalCount)}</TotalValue>
                                                    {hasTranscripts && showPercentOfTotal && displayData.length > 1 && (
                                                        <TotalPercent>
                                                            {displayDataTotalCount !== 0 ? Math.min(100, Math.round((location.totalCount / displayDataTotalCount) * 100)) : 0}% of {selectedCategoryFilters.size > 0 ? Array.from(selectedCategoryFilters).map(getCategoryDisplayNamePlural).join(' & ') : 'conversations'}
                                                        </TotalPercent>
                                                    )}
                                                </LocationTotal>
                                            </LocationStats>
                                            {hasLocationTranscripts && (
                                                <ExpandIcon isExpanded={isExpanded}>
                                                    ▼
                                                </ExpandIcon>
                                            )}
                                        </LocationHeader>

                                        {isExpanded && hasLocationTranscripts && (
                                            <SessionsContainer>
                                                <SessionsList>
                                                    {locationTranscripts.map((transcript) => {
                                                        const isTranscriptExpanded = expandedTranscripts.has(transcript.id);
                                                        const summaries = parseSessionSummaries(transcript.sessionSummaries, transcript.sessionId);
                                                        const hasSummaries = summaries.length > 0;
                                                        // Use noSummaryText as fallback when no summaries exist
                                                        const hasExpandableContent = hasSummaries || !!noSummaryText;

                                                        return (
                                                            <TranscriptItem key={transcript.id}>
                                                                <SessionRow
                                                                    as="div"
                                                                    onClick={() => hasExpandableContent && toggleTranscriptExpansion(transcript.id)}
                                                                    onKeyDown={(e: React.KeyboardEvent) => {
                                                                        // Don't trigger row expansion for events originating from SessionLink
                                                                        if ((e.target as HTMLElement).closest('a')) return;
                                                                        if (!hasExpandableContent) return;
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            toggleTranscriptExpansion(transcript.id);
                                                                        }
                                                                    }}
                                                                    tabIndex={hasExpandableContent ? 0 : undefined}
                                                                    role={hasExpandableContent ? "button" : undefined}
                                                                    isExpandable={hasExpandableContent}
                                                                    isExpanded={isTranscriptExpanded}
                                                                >
                                                                    {hasExpandableContent && (
                                                                        <TranscriptExpandIcon isExpanded={isTranscriptExpanded}>
                                                                            ▶
                                                                        </TranscriptExpandIcon>
                                                                    )}
                                                                    <SessionDateTime>
                                                                        <SessionDate>{transcript.date}</SessionDate>
                                                                        <SessionTime>{transcript.time}</SessionTime>
                                                                    </SessionDateTime>
                                                                    <SessionPhone>***{transcript.phoneLastFour}</SessionPhone>
                                                                    {transcript.category && (
                                                                        <SessionCategory category={transcript.category}>
                                                                            {(() => {
                                                                                // Use getCategoryDisplayName for success-type categories
                                                                                const displayCategory = getCategoryDisplayName(transcript.category) !== transcript.category
                                                                                    ? getCategoryDisplayName(transcript.category)
                                                                                    : transcript.category.replace(/_/g, ' ').replace(/^(PLANNED|UNPLANNED|UNDETERMINED)\s*-\s*/i, '');
                                                                                // For healthcare domain bookings, show multipatient count
                                                                                if (domain === 'HEALTHCARE' && transcript.tags) {
                                                                                    const tagToCount = transcript.category.toLowerCase().includes('booking')
                                                                                        ? 'ACTION_COMPLETED'
                                                                                        : transcript.category.toLowerCase().includes('reschedule')
                                                                                            ? 'APPOINTMENT_RESCHEDULED'
                                                                                            : transcript.category.toLowerCase().includes('cancel')
                                                                                                ? 'APPOINTMENT_CANCELLED'
                                                                                                : null;
                                                                                    if (tagToCount) {
                                                                                        const count = transcript.tags.filter(tag => tag === tagToCount).length;
                                                                                        if (count > 1) {
                                                                                            return `${displayCategory} (${count})`;
                                                                                        }
                                                                                    }
                                                                                }
                                                                                return displayCategory;
                                                                            })()}
                                                                        </SessionCategory>
                                                                    )}
                                                                    <SessionLink
                                                                        href={`/v/apps/${transcript.applicationId}/analytics/sessions?sessionId=${transcript.sessionId}&startDate=${encodeURIComponent(moment(dateRange.startDate).startOf('day').toISOString())}&endDate=${encodeURIComponent(moment(dateRange.endDate).endOf('day').toISOString())}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        View Transcript →
                                                                    </SessionLink>
                                                                </SessionRow>
                                                                {isTranscriptExpanded && hasExpandableContent && (
                                                                    <ConversationSummariesContainer>
                                                                        {hasSummaries ? (
                                                                            summaries.map((summary, idx) => {
                                                                                const isEmpty = !summary || !summary.trim();
                                                                                const isCategoryPlaceholder = summary && ['IMMEDIATE_TRANSFER', 'NO_INTERACTION'].includes(summary.trim());
                                                                                const needsFallback = isEmpty || isCategoryPlaceholder;
                                                                                const displayText = needsFallback
                                                                                    ? `• ${getFallbackMessage(isCategoryPlaceholder ? summary.trim() : transcript.category)}`
                                                                                    : summary;
                                                                                return (
                                                                                    <ConversationSummary key={idx}>
                                                                                        {summaries.length > 1 && (
                                                                                            <ConversationLabel>Call #{idx + 1}</ConversationLabel>
                                                                                        )}
                                                                                        <ConversationText>
                                                                                            {renderSummaryWithBullets(displayText)}
                                                                                        </ConversationText>
                                                                                    </ConversationSummary>
                                                                                );
                                                                            })
                                                                        ) : (
                                                                            <ConversationSummary>
                                                                                <ConversationText>
                                                                                    {renderSummaryWithBullets(`• ${getFallbackMessage(transcript.category)}`)}
                                                                                </ConversationText>
                                                                            </ConversationSummary>
                                                                        )}
                                                                    </ConversationSummariesContainer>
                                                                )}
                                                            </TranscriptItem>
                                                        );
                                                    })}
                                                </SessionsList>
                                            </SessionsContainer>
                                        )}
                                    </LocationItem>
                                );
                            })}
                        </LocationsList>
                    )}
                </ScrollContainer>

                {/* Footer */}
                <ModalFooter>
                    <ExportButton onClick={handleExportCSV} disabled={tableData.length === 0}>
                        Export to CSV
                    </ExportButton>
                </ModalFooter>
            </ModalContent>
        </ModalLayout>
    );
};

// Styled Components
const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: linear-gradient(180deg, ${color_defaults_white} 0%, ${color_shades_lighter} 100%);
    overflow: hidden;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid ${color_shades_light};
    flex-shrink: 0;
    background: ${color_defaults_white};
    border-radius: 8px 8px 0 0;

    ${breakpoint_macbook_small} {
        padding: 16px 20px;
    }

    ${breakpoint_large} {
        padding: 12px 16px;
    }
`;

const TitleSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ModalTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const ModalTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: ${color_shades_darkest};
    margin: 0;

    ${breakpoint_macbook_small} {
        font-size: 17px;
    }

    ${breakpoint_large} {
        font-size: 15px;
    }
`;

const SpanishBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${voicify_spark_darker};
    color: white;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 4px;
    letter-spacing: 0.5px;
`;

const DateRange = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${color_shades_darker};

    ${breakpoint_macbook_small} {
        font-size: 12px;
    }

    ${breakpoint_large} {
        font-size: 11px;
    }
`;

const HeaderRightSection = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const HeaderInfoText = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${voicify_spark_darker};
    background: linear-gradient(135deg, ${voicify_spark_lightest} 0%, rgba(14, 133, 187, 0.08) 100%);
    padding: 8px 16px;
    border-radius: 20px;
    white-space: nowrap;

    ${breakpoint_macbook_small} {
        font-size: 13px;
        padding: 7px 14px;
    }

    ${breakpoint_large} {
        font-size: 12px;
        padding: 6px 12px;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 28px;
    color: ${color_text_dashboard_muted};
    cursor: pointer;
    padding: 0;
    line-height: 1;

    &:hover {
        color: ${color_shades_darkest};
    }

    ${breakpoint_macbook_small} {
        font-size: 24px;
    }

    ${breakpoint_large} {
        font-size: 20px;
    }
`;

const SummarySection = styled.div`
    display: flex;
    gap: 16px;
    padding: 24px 32px;
    background: ${color_defaults_white};
    border-bottom: 1px solid ${color_shades_light};
    flex-wrap: wrap;

    ${breakpoint_macbook_small} {
        padding: 16px 24px;
        gap: 12px;
    }

    ${breakpoint_large} {
        padding: 12px 20px;
        gap: 8px;
    }
`;

const SummaryCard = styled.div<{ isPrimary?: boolean }>`
    display: flex;
    flex-direction: column;
    padding: 16px 24px;
    background: ${props => props.isPrimary
        ? `linear-gradient(135deg, ${voicify_spark_darkest} 0%, ${voicify_spark_darker} 100%)`
        : color_defaults_white};
    border-radius: 12px;
    box-shadow: ${props => props.isPrimary
        ? '0 4px 12px rgba(45, 45, 57, 0.25)'
        : '0 2px 8px rgba(0, 0, 0, 0.06)'};
    min-width: 130px;
    border: ${props => props.isPrimary ? 'none' : `1px solid ${color_shades_light}`};
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: ${props => props.isPrimary
        ? '0 6px 16px rgba(45, 45, 57, 0.3)'
        : '0 4px 12px rgba(0, 0, 0, 0.1)'};
    }

    ${breakpoint_macbook_small} {
        padding: 12px 18px;
        min-width: 110px;
    }

    ${breakpoint_large} {
        padding: 10px 14px;
        min-width: 100px;
    }
`;

const SummaryLabel = styled.span<{ isPrimary?: boolean }>`
    font-size: 11px;
    font-weight: 600;
    color: ${props => props.isPrimary ? 'rgba(255, 255, 255, 0.7)' : color_text_dashboard_muted};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
`;

const SummaryValue = styled.span<{ isPrimary?: boolean }>`
    font-size: 24px;
    font-weight: 700;
    color: ${props => props.isPrimary ? color_defaults_white : voicify_spark_darkest};
    letter-spacing: -0.5px;
    line-height: 1.1;

    ${breakpoint_macbook_small} {
        font-size: 20px;
    }

    ${breakpoint_large} {
        font-size: 18px;
    }
`;

const SummarySubtext = styled.span<{ isPrimary?: boolean }>`
    font-size: 12px;
    font-weight: 500;
    color: ${props => props.isPrimary ? 'rgba(255, 255, 255, 0.6)' : color_shades_darker};
    margin-top: 4px;
`;

const FilterSection = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 32px;
    background: ${color_defaults_white};
    border-bottom: 1px solid ${color_shades_light};

    ${breakpoint_macbook_small} {
        padding: 10px 24px;
        gap: 10px;
    }

    ${breakpoint_large} {
        padding: 8px 20px;
        gap: 8px;
    }
`;

const FilterLabel = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${color_shades_darker};
    flex-shrink: 0;

    ${breakpoint_macbook_small} {
        font-size: 12px;
    }

    ${breakpoint_large} {
        font-size: 11px;
    }
`;

const FilterChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const FilterChip = styled.button<{ isActive: boolean; category?: string }>`
    padding: 6px 14px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid ${props => {
        if (!props.isActive) return color_shades_light;
        if (props.category === 'Success' || props.category === 'Booking' || props.category === 'Event') return '#28a745';
        if (props.category === 'Attempt') return voicify_spark_darker;
        return voicify_spark_medium;
    }};
    background: ${props => {
        if (!props.isActive) return color_defaults_white;
        if (props.category === 'Success' || props.category === 'Booking' || props.category === 'Event') return 'rgba(40, 167, 69, 0.1)';
        if (props.category === 'Attempt') return 'rgba(14, 133, 187, 0.1)';
        return voicify_spark_lightest;
    }};
    color: ${props => {
        if (!props.isActive) return color_shades_darker;
        if (props.category === 'Success' || props.category === 'Booking' || props.category === 'Event') return '#28a745';
        if (props.category === 'Attempt') return voicify_spark_darker;
        return voicify_spark_darkest;
    }};

    &:hover {
        border-color: ${props => {
        if (props.category === 'Success' || props.category === 'Booking' || props.category === 'Event') return '#28a745';
        if (props.category === 'Attempt') return voicify_spark_darker;
        return voicify_spark_medium;
    }};
        background: ${props => {
        if (props.category === 'Success' || props.category === 'Booking' || props.category === 'Event') return 'rgba(40, 167, 69, 0.15)';
        if (props.category === 'Attempt') return 'rgba(14, 133, 187, 0.15)';
        return voicify_spark_lightest;
    }};
    }

    ${breakpoint_macbook_small} {
        padding: 5px 12px;
        font-size: 11px;
    }

    ${breakpoint_large} {
        padding: 4px 10px;
        font-size: 11px;
    }
`;

const ScrollContainer = styled.div`
    flex: 1;
    padding: 24px 32px;
    overflow-y: auto;
    min-height: 0;
    background: ${color_defaults_white};

    ${breakpoint_macbook_small} {
        padding: 16px 24px;
    }

    ${breakpoint_large} {
        padding: 12px 20px;
    }
`;

const DataTable = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid ${color_shades_light};
`;

const TableHeader = styled.th`
    text-align: left;
    padding: 14px 20px;
    background: linear-gradient(180deg, ${voicify_spark_lightest} 0%, ${color_shades_lighter} 100%);
    font-size: 11px;
    font-weight: 700;
    color: ${voicify_spark_darker};
    text-transform: uppercase;
    letter-spacing: 0.8px;
    border-bottom: 2px solid ${voicify_spark_light};
    position: sticky;
    top: 0;
    z-index: 1;

    ${breakpoint_macbook_small} {
        padding: 10px 16px;
        font-size: 10px;
    }

    ${breakpoint_large} {
        padding: 8px 12px;
        font-size: 10px;
    }
`;

const TableRow = styled.tr<{ isEven: boolean }>`
    background: ${props => props.isEven ? 'rgba(245, 247, 250, 0.5)' : color_defaults_white};
    transition: background 0.15s ease;

    &:hover {
        background: rgba(14, 133, 187, 0.04);
    }
`;

const RankCell = styled.td`
    padding: 14px 12px;
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    color: ${voicify_spark_dark};
    width: 40px;

    ${breakpoint_macbook_small} {
        padding: 10px 10px;
        font-size: 13px;
    }

    ${breakpoint_large} {
        padding: 8px 8px;
        font-size: 12px;
    }
`;

const LocationCell = styled.td`
    padding: 14px 20px;
    vertical-align: middle;

    ${breakpoint_macbook_small} {
        padding: 10px 16px;
    }

    ${breakpoint_large} {
        padding: 8px 12px;
    }
`;

const LocationName = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${voicify_spark_darkest};

    ${breakpoint_macbook_small} {
        font-size: 13px;
    }

    ${breakpoint_large} {
        font-size: 12px;
    }
`;

const NumberCell = styled.td`
    padding: 14px 20px;
    font-size: 14px;
    font-weight: 600;
    color: ${color_shades_darkest};
    text-align: right;
    font-variant-numeric: tabular-nums;

    ${breakpoint_macbook_small} {
        padding: 10px 16px;
        font-size: 13px;
    }

    ${breakpoint_large} {
        padding: 8px 12px;
        font-size: 12px;
    }
`;

const LanguagePercent = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: ${color_shades_darker};
    margin-left: 4px;

    ${breakpoint_macbook_small} {
        font-size: 11px;
    }

    ${breakpoint_large} {
        font-size: 10px;
    }
`;

const PercentCell = styled.td`
    padding: 14px 20px;
    text-align: right;
    width: 140px;

    ${breakpoint_macbook_small} {
        padding: 10px 16px;
        width: 120px;
    }

    ${breakpoint_large} {
        padding: 8px 12px;
        width: 100px;
    }
`;

const PercentBar = styled.div<{ percent: number }>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    position: relative;
    
    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        height: 6px;
        width: ${props => Math.min(props.percent, 100)}%;
        background: linear-gradient(90deg, ${voicify_spark_light} 0%, ${voicify_spark_medium} 100%);
        border-radius: 3px;
        opacity: 0.6;
    }
`;

const PercentValue = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${voicify_spark_darker};
    font-variant-numeric: tabular-nums;
    position: relative;
    z-index: 1;

    ${breakpoint_macbook_small} {
        font-size: 13px;
    }

    ${breakpoint_large} {
        font-size: 12px;
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 40px;
    text-align: center;
`;

const EmptyStateTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: ${color_shades_darkest};
    margin: 0 0 8px 0;

    ${breakpoint_macbook_small} {
        font-size: 16px;
    }

    ${breakpoint_large} {
        font-size: 14px;
    }
`;

const EmptyStateText = styled.p`
    font-size: 14px;
    color: ${color_shades_darker};
    margin: 0;
    max-width: 300px;

    ${breakpoint_macbook_small} {
        font-size: 13px;
    }

    ${breakpoint_large} {
        font-size: 12px;
    }
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 20px 32px;
    border-top: 1px solid ${color_shades_light};
    background: ${color_defaults_white};

    ${breakpoint_macbook_small} {
        padding: 14px 24px;
    }

    ${breakpoint_large} {
        padding: 12px 20px;
    }
`;

const ExportButton = styled.button`
    padding: 12px 28px;
    background: linear-gradient(135deg, ${voicify_spark_darkest} 0%, ${voicify_spark_darker} 100%);
    color: white;
    border: none;
    border-radius: 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(45, 45, 57, 0.2);

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(45, 45, 57, 0.3);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        background: ${color_shades_light};
        color: ${color_text_dashboard_muted};
        cursor: not-allowed;
        box-shadow: none;
    }

    ${breakpoint_macbook_small} {
        padding: 10px 22px;
        font-size: 13px;
    }

    ${breakpoint_large} {
        padding: 8px 18px;
        font-size: 13px;
    }
`;

// Tab Navigation Styled Components (keeping for potential future use)
const TabNavigation = styled.div`
    display: flex;
    gap: 0;
    padding: 0 32px;
    background: ${color_defaults_white};
    border-bottom: 1px solid ${color_shades_light};

    ${breakpoint_macbook_small} {
        padding: 0 24px;
    }

    ${breakpoint_large} {
        padding: 0 20px;
    }
`;

const TabButton = styled.button<{ isActive: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 24px;
    background: none;
    border: none;
    border-bottom: 3px solid ${props => props.isActive ? voicify_spark_medium : 'transparent'};
    font-size: 14px;
    font-weight: ${props => props.isActive ? 600 : 500};
    color: ${props => props.isActive ? voicify_spark_darkest : color_shades_darker};
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: -1px;

    &:hover {
        color: ${voicify_spark_darkest};
        background: rgba(14, 133, 187, 0.04);
    }

    ${breakpoint_macbook_small} {
        padding: 12px 20px;
        font-size: 13px;
    }

    ${breakpoint_large} {
        padding: 10px 16px;
        font-size: 12px;
    }
`;

const TabIcon = styled.span`
    font-size: 16px;
`;

const TabBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    background: linear-gradient(135deg, ${voicify_spark_light} 0%, ${voicify_spark_medium} 100%);
    color: ${color_defaults_white};
    font-size: 11px;
    font-weight: 700;
    border-radius: 10px;
    min-width: 20px;
`;

// Expandable Location List Styled Components
const LocationsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const LocationItem = styled.div`
    background: ${color_defaults_white};
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid ${color_shades_light};
`;

const LocationHeader = styled.div<{ isExpandable: boolean; isExpanded: boolean }>`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    cursor: ${props => props.isExpandable ? 'pointer' : 'default'};
    transition: all 0.2s ease;
    background: ${props => props.isExpanded ? `linear-gradient(135deg, ${voicify_spark_lightest} 0%, rgba(14, 133, 187, 0.05) 100%)` : color_defaults_white};
    border-bottom: ${props => props.isExpanded ? `1px solid ${color_shades_light}` : 'none'};
    border-radius: ${props => props.isExpanded ? '12px 12px 0 0' : '12px'};

    &:hover {
        background: ${props => props.isExpandable
        ? `linear-gradient(135deg, ${voicify_spark_lightest} 0%, rgba(14, 133, 187, 0.05) 100%)`
        : color_defaults_white};
    }

    &:focus {
        box-shadow: ${props => props.isExpandable ? `inset 0 0 0 2px ${voicify_spark_medium}` : 'none'};
        outline: none;
    }

    &:focus-visible {
        box-shadow: ${props => props.isExpandable ? `inset 0 0 0 2px ${voicify_spark_medium}` : 'none'};
        outline: none;
    }

    ${breakpoint_macbook_small} {
        padding: 12px 16px;
        gap: 12px;
    }

    ${breakpoint_large} {
        padding: 10px 14px;
        gap: 10px;
    }
`;

const LocationRank = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, ${voicify_spark_light} 0%, ${voicify_spark_medium} 100%);
    color: ${color_defaults_white};
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
`;

const LocationInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const LocationNameExpand = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${voicify_spark_darkest};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    ${breakpoint_macbook_small} {
        font-size: 13px;
    }

    ${breakpoint_large} {
        font-size: 12px;
    }
`;

const SessionCount = styled.div`
    font-size: 12px;
    color: ${color_shades_darker};
    margin-top: 2px;

    ${breakpoint_macbook_small} {
        font-size: 11px;
    }

    ${breakpoint_large} {
        font-size: 10px;
    }
`;

const LocationStats = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;

    ${breakpoint_macbook_small} {
        gap: 16px;
    }

    ${breakpoint_large} {
        gap: 12px;
    }
`;

const LocationTotal = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
`;

const TotalValue = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: ${voicify_spark_darkest};

    ${breakpoint_macbook_small} {
        font-size: 14px;
    }

    ${breakpoint_large} {
        font-size: 13px;
    }
`;

const TotalPercent = styled.span`
    font-size: 11px;
    font-weight: 500;
    color: ${voicify_spark_dark};
`;

const LanguageBreakdownSmall = styled.div`
    display: flex;
    gap: 12px;

    ${breakpoint_macbook_small} {
        gap: 8px;
    }

    ${breakpoint_large} {
        gap: 6px;
    }
`;

const LangItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
`;

const LangLabel = styled.span`
    font-size: 11px;
    font-weight: 500;
    color: ${color_shades_darker};
`;

const LangValue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${color_shades_darkest};

    ${breakpoint_macbook_small} {
        font-size: 12px;
    }

    ${breakpoint_large} {
        font-size: 11px;
    }
`;

const ExpandIcon = styled.span<{ isExpanded: boolean }>`
    font-size: 10px;
    color: ${voicify_spark_dark};
    transition: transform 0.2s ease;
    transform: rotate(${props => props.isExpanded ? '180deg' : '0deg'});
    margin-left: 8px;
`;

const SessionsContainer = styled.div`
    background: ${color_shades_lighter};
    padding: 0;
`;

const SessionsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    background: rgba(14, 133, 187, 0.03);
    border-bottom: 1px solid ${color_shades_light};

    ${breakpoint_macbook_small} {
        padding: 10px 16px;
    }

    ${breakpoint_large} {
        padding: 8px 14px;
    }
`;

const SessionsTitle = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${voicify_spark_darker};

    ${breakpoint_macbook_small} {
        font-size: 11px;
    }

    ${breakpoint_large} {
        font-size: 10px;
    }
`;

const SessionsCount = styled.span`
    font-size: 11px;
    color: ${color_shades_darker};
`;

const SessionsList = styled.div`
    max-height: 400px;
    overflow-y: auto;

    ${breakpoint_macbook_small} {
        max-height: 320px;
    }

    ${breakpoint_large} {
        max-height: 280px;
    }
`;

const TranscriptItem = styled.div`
    border-bottom: 1px solid ${color_shades_lighter};

    &:last-child {
        border-bottom: none;
    }
`;

const SessionRow = styled.div<{ isExpandable?: boolean; isExpanded?: boolean }>`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 20px;
    background: ${props => props.isExpanded ? voicify_spark_lightest : color_defaults_white};
    transition: background 0.15s ease;
    cursor: ${props => props.isExpandable ? 'pointer' : 'default'};

    &:hover {
        background: ${voicify_spark_lightest};
    }

    ${breakpoint_macbook_small} {
        padding: 10px 16px;
        gap: 12px;
    }

    ${breakpoint_large} {
        padding: 8px 14px;
        gap: 10px;
    }
`;

const TranscriptExpandIcon = styled.span<{ isExpanded: boolean }>`
    font-size: 8px;
    color: ${voicify_spark_dark};
    transition: transform 0.2s ease;
    transform: rotate(${props => props.isExpanded ? '90deg' : '0deg'});
    flex-shrink: 0;
    width: 12px;
`;

const ConversationSummariesContainer = styled.div`
    padding: 12px 20px 12px 44px;
    background: ${color_shades_lighter};
    border-top: 1px solid ${color_shades_light};

    ${breakpoint_macbook_small} {
        padding: 10px 16px 10px 36px;
    }

    ${breakpoint_large} {
        padding: 8px 14px 8px 30px;
    }
`;

const ConversationSummary = styled.div`
    padding: 10px 14px;
    background: ${color_defaults_white};
    border-radius: 8px;
    border: 1px solid ${color_shades_light};
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const ConversationLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${voicify_spark_darker};
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 6px;
`;

const ConversationText = styled.div`
    font-size: 13px;
    color: ${color_shades_darkest};
    line-height: 1.5;

    ${breakpoint_macbook_small} {
        font-size: 12px;
    }

    ${breakpoint_large} {
        font-size: 11px;
    }
`;

const SummaryBulletList = styled.ul`
    margin: 0;
    padding-left: 20px;
    list-style-type: disc;
`;

const SummaryBulletItem = styled.li`
    margin-bottom: 4px;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const SummaryParagraph = styled.p`
    margin: 0 0 8px 0;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const SessionDateTime = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 150px;

    ${breakpoint_macbook_small} {
        min-width: 130px;
        gap: 6px;
    }

    ${breakpoint_large} {
        min-width: 110px;
        gap: 4px;
    }
`;

const SessionDate = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${color_shades_darkest};

    ${breakpoint_macbook_small} {
        font-size: 12px;
    }

    ${breakpoint_large} {
        font-size: 11px;
    }
`;

const SessionTime = styled.span`
    font-size: 12px;
    color: ${color_shades_darker};
    padding: 2px 6px;
    background: ${color_shades_lighter};
    border-radius: 4px;

    ${breakpoint_macbook_small} {
        font-size: 11px;
    }

    ${breakpoint_large} {
        font-size: 10px;
        padding: 1px 4px;
    }
`;

const SessionPhone = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: ${color_shades_darker};
    font-family: monospace;
    min-width: 70px;

    ${breakpoint_macbook_small} {
        font-size: 11px;
        min-width: 60px;
    }

    ${breakpoint_large} {
        font-size: 10px;
        min-width: 55px;
    }
`;

const categoryColors: Record<string, { bg: string; color: string }> = {
    'ACTION_COMPLETED': { bg: 'rgba(40, 167, 69, 0.1)', color: '#28a745' },
    'ACTION_ATTEMPTED': { bg: 'rgba(14, 133, 187, 0.1)', color: voicify_spark_darker },
    'TRANSFERRED': { bg: 'rgba(255, 193, 7, 0.15)', color: '#d39e00' },
    'NO_ACTION': { bg: 'rgba(108, 117, 125, 0.1)', color: '#6c757d' },
    'Booking': { bg: 'rgba(40, 167, 69, 0.1)', color: '#28a745' },
    'Event': { bg: 'rgba(40, 167, 69, 0.1)', color: '#28a745' },
    'Success': { bg: 'rgba(40, 167, 69, 0.1)', color: '#28a745' },
    'Attempt': { bg: 'rgba(14, 133, 187, 0.1)', color: voicify_spark_darker },
    'default': { bg: 'rgba(108, 117, 125, 0.1)', color: '#6c757d' }
};

const SessionCategory = styled.span<{ category?: string }>`
    display: inline-flex;
    align-items: center;
    padding: 3px 8px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-radius: 4px;
    background: ${props => categoryColors[props.category || 'default']?.bg || categoryColors.default.bg};
    color: ${props => categoryColors[props.category || 'default']?.color || categoryColors.default.color};
    flex-shrink: 0;
`;

const SessionLink = styled.a`
    margin-left: auto;
    font-size: 12px;
    font-weight: 600;
    color: ${voicify_spark_dark};
    text-decoration: none;
    padding: 4px 10px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 4px;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        color: ${voicify_spark_darkest};
        background: ${voicify_spark_lightest};
        border-color: ${voicify_spark_light};
    }
`;

export default ChartDetailModal;
